/* eslint-disable no-useless-escape */
import { ErrorHandler, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoadingService } from '../loading.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorDialogComponent } from '@awo-care/shared-ui';
import { GlobalError, ErrorLogSnackbarType } from '../../types/error.types';
import { classifyConnectivityError } from './connectivity-error.utils';

export interface ErrorData {
  comment: string;
  errorData: unknown;
  uid: string;
  userEmail: string;
  tierName: string;
  appType: 'customer' | 'employee' | 'website';
  appVersion: string;
  currentUrl?: string;
}

@Injectable({ providedIn: 'root' })
export abstract class BaseErrorHandlerService implements ErrorHandler {
  protected loadingService = inject(LoadingService);
  private snackBar = inject(MatSnackBar);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('unhandledrejection', (e) => {
        this.handleError(e.reason);
        e.preventDefault();
      });

      // Capture global errors with full details (before they become "Script error.")
      window.onerror = (message, source, lineno, colno, error) => {
        // "Script error." with no details = cross-origin or in-app browser limitation
        if (message === 'Script error.' && !error) {
          this.handleError({
            name: 'ScriptError',
            message: 'Script error (no details available — likely in-app browser or cross-origin)',
            code: 'ERR_SCRIPT',
            bugReport: 'none' as ErrorLogSnackbarType,
            stack: source ? `${source}:${lineno}:${colno}` : '',
            context: {
              userAgent: navigator.userAgent,
              isInAppBrowser: this.detectInAppBrowser(),
            },
          });
          return true; // Prevent default browser error logging
        }

        // For same-origin errors, let Angular's Zone.js handle them
        return false;
      };
    }
  }

  private detectInAppBrowser(): string | false {
    const ua = navigator.userAgent;
    if (/FBAN|FBAV/.test(ua)) return 'Facebook';
    if (/Instagram/.test(ua)) return 'Instagram';
    if (/LinkedIn/.test(ua)) return 'LinkedIn';
    if (/Twitter/.test(ua)) return 'Twitter/X';
    // iOS WebView (not Safari, not Chrome)
    if (/iPhone|iPad/.test(ua) && !/Safari/.test(ua)) return 'iOS WebView';
    // Generic WebView detection
    if (/wv|WebView/.test(ua)) return 'WebView';
    return false;
  }

  protected abstract getUserId(): string;
  protected abstract getUserEmail(): string;
  protected abstract getAppVersion(): string;
  protected abstract getEnvironment(): string;

  private lastErrorTime = 0;
  private debounceTime = 10000;
  private readonly DEFAULT_MESSAGES = {
    NAME: 'UnknownError',
    HEADER: 'Unbekannter Fehler',
    MESSAGE: 'Ein unbekannter Fehler ist aufgetreten',
    CODE: 'ERR_001',
  } as const;

  async handleError(error: unknown): Promise<void> {
    try {
      // Add stack trace to help identify where null errors originate
      if (error === null || error === undefined) {
        console.warn('ErrorHandler -> null/undefined error detected (ignoring)', {
          error,
          stack: new Error().stack,
        });
        // Silently ignore null/undefined errors as they are likely from unhandled promise rejections
        // that were already handled elsewhere
        return;
      } else {
        console.error('ErrorHandler ->', error);
      }

      const currentTime = Date.now();
      if (currentTime - this.lastErrorTime < this.debounceTime) return;

      this.lastErrorTime = currentTime;
      const errorData = this.parseError(error);

      const sendErrorData: ErrorData = {
        errorData,
        comment: '',
        uid: this.getUserId(),
        userEmail: this.getUserEmail(),
        tierName: this.getEnvironment(),
        appType: this.getAppType(),
        appVersion: this.getAppVersion(),
        currentUrl: typeof window !== 'undefined' ? window.location.href : '',
      };

      await this.processError(sendErrorData, errorData);
    } catch (error) {
      console.error('Error in error handler:', error);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  protected abstract getAppType(): 'customer' | 'employee' | 'website';
  protected abstract processError(
    sendErrorData: ErrorData,
    errorData: GlobalError
  ): Promise<void>;

  protected parseError(error: unknown): GlobalError {
    const errorObj = this.normalizeError(error);

    const parsed: GlobalError = {
      name: this.getStringValue(errorObj['name'], this.DEFAULT_MESSAGES.NAME),
      header: this.getStringValue(
        errorObj['header'],
        this.DEFAULT_MESSAGES.HEADER
      ),
      message: this.getStringValue(
        errorObj['message'],
        this.DEFAULT_MESSAGES.MESSAGE
      ),
      code: this.getStringValue(errorObj['code'], this.DEFAULT_MESSAGES.CODE),
      stack: this.truncateAndSanitizeStack(
        this.getStringValue(errorObj['stack'], '')
      ),
      bugReport: (errorObj['bugReport'] as ErrorLogSnackbarType) ?? 'manual',
      ...(errorObj['context'] ? { context: errorObj['context'] as Record<string, unknown> } : {}),
    };

    // Bereits aufbereitete Fehler (eigener Header gesetzt) bleiben unangetastet —
    // Komponenten wie der Login formulieren ihre Meldungen selbst.
    if (errorObj['header']) {
      return parsed;
    }

    // Funkloch, Firewall oder blockiertes reCAPTCHA sind keine Bugs: Der Nutzer
    // braucht einen Handlungshinweis, und der Issue-Bot darf damit nicht
    // geflutet werden.
    const connectivity = classifyConnectivityError(
      parsed.code,
      parsed.message,
      isPlatformBrowser(this.platformId) && typeof navigator !== 'undefined'
        ? navigator.onLine
        : undefined
    );

    if (connectivity) {
      return {
        ...parsed,
        header: connectivity.header,
        message: connectivity.message,
        bugReport: 'none',
      };
    }

    return parsed;
  }

  private getStringValue(value: unknown, defaultValue: string): string {
    return typeof value === 'string' ? value : defaultValue;
  }

  private normalizeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      const { name, message, stack, ...rest } = error;
      return { name, message, stack, ...rest };
    }
    if (typeof error === 'object' && error !== null) {
      return error as Record<string, unknown>;
    }
    return { message: String(error) };
  }

  private truncateAndSanitizeStack(stack: string): string {
    if (!stack) return '';
    const lines = stack.split('\n').slice(0, 10);
    const truncatedStack = lines.join('\n').slice(0, 2000);
    return truncatedStack.replace(/[^\w\s\-\(\)\{\}\[\].,;:!?/@:#=&+~'"]/g, '');
  }

  protected showErrorSnackbar(error: GlobalError): void {
    this.snackBar.openFromComponent(ErrorDialogComponent, {
      data: error,
      duration: 10000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }
}
